import { useLayoutEffect } from 'react';

const STORAGE_KEY = 'executionsScrollAnchor';
const CARD_SELECTOR = '[data-execution-id]';
const DETAIL_PATH_PREFIX = '/campaigns/';

interface ScrollAnchor {
  /** Execution whose card was the first one visible when leaving the list. */
  executionId: string | null;
  /** Distance from that card's top edge to the top of the viewport. */
  offsetTop: number;
  /** Raw window scroll, used when the anchor card is no longer in the list. */
  scrollY: number;
}

/**
 * Brings the user back to where they were in the executions list after opening
 * an execution's detail (or report) and returning.
 *
 * Cards load their metrics lazily, so their heights change after mount and a
 * raw scrollY lands in the wrong place. Instead we remember the first visible
 * card and its offset in the viewport, and scroll that card back to the same
 * spot once the list has rendered.
 */
export function useExecutionsScrollRestoration(isReady: boolean): void {
  useLayoutEffect(() => {
    if (!isReady) return;

    restoreAnchor();

    return () => {
      // Cleanup runs before the list's DOM is removed and after the router has
      // updated the URL, so we can tell where the user is going.
      if (window.location.pathname.startsWith(DETAIL_PATH_PREFIX)) {
        saveAnchor();
      }
    };
  }, [isReady]);
}

function saveAnchor(): void {
  const firstVisibleCard = findFirstVisibleCard();
  const anchor: ScrollAnchor = {
    executionId: firstVisibleCard?.dataset.executionId ?? null,
    offsetTop: firstVisibleCard?.getBoundingClientRect().top ?? 0,
    scrollY: window.scrollY,
  };
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(anchor));
  } catch {
    // Storage unavailable (private mode) — restoring is best-effort.
  }
}

function restoreAnchor(): void {
  const anchor = readAnchor();
  if (!anchor) return;
  sessionStorage.removeItem(STORAGE_KEY);

  const card = anchor.executionId
    ? document.querySelector<HTMLElement>(`[data-execution-id="${CSS.escape(anchor.executionId)}"]`)
    : null;
  const top = card
    ? card.getBoundingClientRect().top + window.scrollY - anchor.offsetTop
    : anchor.scrollY;
  window.scrollTo({ top, behavior: 'instant' });
}

function findFirstVisibleCard(): HTMLElement | null {
  const cards = document.querySelectorAll<HTMLElement>(CARD_SELECTOR);
  for (const card of cards) {
    if (card.getBoundingClientRect().bottom > 0) return card;
  }
  return null;
}

function readAnchor(): ScrollAnchor | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ScrollAnchor) : null;
  } catch {
    return null;
  }
}
